<template>
  <div class="inertia-pro-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ activeFormula }}$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2; min-height: 280px;">
        <svg width="100%" height="100%" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-inertia"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
              <linearGradient id="grad-mass" x1="0" y1="0" x2="1" y2="1">
                 <stop offset="0%" stop-color="#38bdf8" />
                 <stop offset="100%" stop-color="#1e40af" />
              </linearGradient>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- 模式 1: 转动惯量 (Rotational Inertia) -->
           <g v-if="mode === 'rotation'" transform="translate(400, 300)">
              <!-- Axis Center -->
              <circle cx="0" cy="0" r="10" fill="#f8fafc" filter="url(#glow-inertia)" />
              <text x="18" y="7" fill="#64748b" font-size="20" font-weight="900">固定转轴</text>

              <!-- Rotating Bar -->
              <line x1="-320" y1="0" x2="320" y2="0" stroke="#1e293b" stroke-width="12" stroke-linecap="round" :transform="`rotate(${angle})`" />
              
              <!-- Mass 1 -->
              <g :transform="`rotate(${angle}) translate(${radius * 7}, 0)`">
                 <circle r="30" fill="url(#grad-mass)" stroke="#fff" stroke-width="3" filter="url(#glow-inertia)" />
                 <text y="50" fill="#38bdf8" font-size="18" font-weight="900" text-anchor="middle">m</text>
              </g>

              <!-- Mass 2 -->
              <g :transform="`rotate(${angle}) translate(${-radius * 7}, 0)`">
                 <circle r="30" fill="url(#grad-mass)" stroke="#fff" stroke-width="3" filter="url(#glow-inertia)" />
                 <text y="50" fill="#38bdf8" font-size="18" font-weight="900" text-anchor="middle">m</text>
              </g>

              <!-- Radius Label -->
              <g :transform="`rotate(${angle})`">
                 <line x1="0" y1="20" :x2="radius * 7" y2="20" stroke="#94a3b8" stroke-width="2" stroke-dasharray="6,4" />
                 <text :x="radius * 3.5" y="45" fill="#94a3b8" font-size="16" text-anchor="middle" font-weight="bold">r</text>
              </g>

              <!-- Alpha Indicator -->
              <path v-if="Math.abs(alpha) > 0.01" d="M -40,-40 A 60 60 0 0 1 40,-40" fill="none" stroke="#fbbf24" stroke-width="4" stroke-linecap="round" stroke-dasharray="10,5">
                 <animateTransform attributeName="transform" type="rotate" from="0" to="360" :dur="`${Math.abs(2/alpha)}s`" repeatCount="indefinite" />
              </path>
           </g>

           <!-- 模式 2: 平移惯性 (Linear Inertia) -->
           <g v-if="mode === 'linear'">
              <line x1="100" y1="450" x2="700" y2="450" stroke="#334155" stroke-width="4" />
              <rect :x="linearPos" y="370" width="120" height="80" fill="url(#grad-mass)" stroke="#fff" stroke-width="3" rx="4" filter="url(#glow-inertia)" />
              <text :x="linearPos + 60" y="350" fill="#38bdf8" font-size="22" font-weight="900" text-anchor="middle">质量 m</text>
              
              <!-- Force Arrow -->
              <g v-if="force > 0" :transform="`translate(${linearPos + 120}, 410)`">
                 <line x1="0" y1="0" :x2="force * 2.5" y2="0" stroke="#f43f5e" stroke-width="8" marker-end="url(#arr-f-inertia)" />
                 <text :x="force * 1.2" y="-15" fill="#f43f5e" font-size="16" font-weight="900" text-anchor="middle">拉力 F</text>
              </g>
              
              <defs>
                 <marker id="arr-f-inertia" markerWidth="10" markerHeight="10" refX="9" refY="5" orient="auto">
                    <polygon points="0 0, 10 5, 0 10" fill="#f43f5e" />
                 </marker>
              </defs>
           </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>研究物理量</label>
              <span class="val">{{ mode === 'rotation' ? '转动惯量 I (kg·m²)' : '惯性质量 m (kg)' }}</span>
           </div>
           <div class="hud-item orange">
              <label>实时加速度</label>
              <span class="val">{{ mode === 'rotation' ? alpha.toFixed(2) + ' rad/s²' : acceleration.toFixed(2) + ' m/s²' }}</span>
           </div>
        </div>
      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">深度原理解析面板</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            <div class="eq-box text-center">
               <div class="eq-display text-blue">物理引擎底层数据</div>
               <div class="seq-data-list">
                  <template v-if="mode === 'rotation'">
                     <div class="data-row"><span>分布半径 (r)</span><span class="hl">{{ (radius/10).toFixed(2) }} m</span></div>
                     <div class="data-row"><span>惯性分布 Σmr²</span><span class="hl text-blue">{{ rotationalInertia.toFixed(2) }} kg·m²</span></div>
                     <div class="data-row"><span>当前角速度 ω</span><span class="hl text-yellow">{{ Math.abs(omega).toFixed(2) }} rad/s</span></div>
                  </template>

                  <template v-if="mode === 'linear'">
                     <div class="data-row"><span>物体惯性 (m)</span><span class="hl text-blue">{{ mass.toFixed(1) }} kg</span></div>
                     <div class="data-row"><span>推进驱动 (F)</span><span class="hl text-red">{{ force.toFixed(1) }} N</span></div>
                     <div class="data-row"><span>当前移动速度 v</span><span class="hl text-green">{{ velocity.toFixed(1) }} m/s</span></div>
                  </template>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心原理揭示：</strong><br/><br/>{{ tipDesc }}</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2;">
               <label>点击切换研究模型：平移惯性 vs 转动惯性</label>
               <el-radio-group v-model="mode" size="small" class="single-row-buttons">
                 <el-radio-button label="linear">平移惯性 (牛顿第二定律)</el-radio-button>
                 <el-radio-button label="rotation">转动惯性 (转动定律)</el-radio-button>
               </el-radio-group>
            </div>
            
            <template v-if="mode === 'rotation'">
               <div class="s-item">
                  <label>调整质量分布半径 r (影响I)</label>
                  <el-slider v-model="radius" :min="5" :max="45" size="small"></el-slider>
               </div>
               <div class="s-item">
                  <label>施加持续扭矩 τ (Torque)</label>
                  <el-slider v-model="torque" :min="0" :max="20" :step="0.5" size="small"></el-slider>
               </div>
            </template>

            <template v-if="mode === 'linear'">
               <div class="s-item">
                  <label>物体质量 m (惯性大小)</label>
                  <el-slider v-model="mass" :min="1" :max="100" size="small"></el-slider>
               </div>
               <div class="s-item">
                  <label>施加水平拉力 F</label>
                  <el-slider v-model="force" :min="0" :max="200" size="small"></el-slider>
               </div>
            </template>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InertiaViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'linear',
      // Rotation params
      radius: 20,
      torque: 5,
      angle: 0,
      omega: 0,
      // Linear params
      mass: 20,
      force: 50,
      linearPos: 100,
      velocity: 0,
      // Logic
      lastTime: 0,
      timer: null
    }
  },
  mounted() {
    this.detectMode();
    this.startLoop();
  },
  beforeDestroy() {
    if (this.timer) cancelAnimationFrame(this.timer);
  },
  watch: {
    knowledgePoint: {
       handler() { this.detectMode(); },
       immediate: true
    }
  },
  computed: {
    topicIntro() {
       return this.mode === 'rotation' ? '转动惯量：为何走钢丝需要横杆？解析旋转中的“阻力”' : '惯性：物体“懒惰”的程度，由其唯一属性——质量决定';
    },
    activeFormula() {
       return this.mode === 'rotation' ? 'τ = Iα | I = Σmr²' : 'F = ma | a = F/m';
    },
    rotationalInertia() {
       // I = 2 * m * r^2. Let base m = 1kg for demo
       const r = this.radius / 10;
       return 2 * 1 * (r * r);
    },
    alpha() {
       return this.torque / (this.rotationalInertia + 0.1);
    },
    acceleration() {
       return this.force / this.mass;
    },
    tipDesc() {
       if (this.mode === 'rotation') return '转动惯量 (I) 是旋转版的“质量”。它最神奇的地方在于：它不仅取决于质量多重，更关键的是质量离转轴【多远】。由于 r 是平方项，半径增加一倍，转动难度会增长四倍！这就是为什么收缩手臂的溜冰者会转得飞快（I 减小，维持角动量守恒）。';
       return '在物理学中，惯性是物体的本征属性。它不是一种力，而是对“改变当前运动状态”的一种天然抵触。你设置的质量 m 越大，同样的推力 F 下，加速度 a 就会越小。大质量物体由于具有更大的惯性，很难启动，但一旦跑起来也很难停下。';
    }
  },
  methods: {
    detectMode() {
      const kp = this.knowledgePoint || '';
      if (/转动惯量/.test(kp)) {
         this.mode = 'rotation';
      } else {
         this.mode = 'linear';
      }
    },
    startLoop() {
       this.lastTime = performance.now();
       const tick = (now) => {
          const dt = Math.min((now - this.lastTime) / 1000, 0.1);
          this.lastTime = now;

          if (this.mode === 'rotation') {
             // τ = Iα
             this.omega += this.alpha * dt;
             // Damping logic
             this.omega *= (this.torque === 0 ? 0.98 : 1.0); 
             if (Math.abs(this.omega) < 0.01) this.omega = 0;
             this.angle += this.omega * 100 * dt; // Scaled for visual speed
          } else {
             // F = ma
             this.velocity += this.acceleration * dt;
             // Simple friction damping when no force
             this.velocity *= (this.force === 0 ? 0.95 : 1.0);
             if (this.velocity < 0.1) this.velocity = 0;
             this.linearPos += this.velocity * 5 * dt;
             if (this.linearPos > 650) this.linearPos = -100;
          }

          this.timer = requestAnimationFrame(tick);
       };
       this.timer = requestAnimationFrame(tick);
    }
  }
}
</script>

<style scoped lang="scss">
.inertia-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 700; } .formula-chip { background: #f0f9ff; color: #0369a1; border: 1px solid #bae6fd; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.split-layout { display: flex; gap: 8px; flex: 1; min-height: 280px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; border: 1.5px solid #1e293b; }
.logic-header { background: #1e293b; padding: 10px; color: #38bdf8; font-size: 13px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; background: rgba(15, 23, 42, 0.6); border: 1px solid #1e293b; }
.eq-display { background: rgba(0,0,0,0.4); padding: 8px; font-weight: 900; border-bottom: 1px solid #1e293b; font-size: 12px; }
.seq-data-list { display: flex; flex-direction: column; }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 14px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 12px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-yellow { color: #facc15; } .text-green { color: #34d399; } .text-blue { color: #38bdf8; } .text-red { color: #f43f5e; }
.desc-text { color: #cbd5e1; font-size: 12px; line-height: 1.6; padding: 12px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; }
.mt-l { margin-top: 15px; }

.simulation-stage { background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }
.hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 6px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; margin-bottom: 2px; } .val { font-size: 14px; color: #f8fafc; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } &.orange { border-left: 3px solid #f59e0b; } } }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; .s-item { label { font-size: 11px; color: #475569; font-weight: 900; margin-bottom: 6px; display: block; } } }
.single-row-buttons { display: flex; flex-wrap: nowrap; overflow-x: auto; width: 100%; .el-radio-button { flex: 1; :deep(.el-radio-button__inner) { width: 100%; padding: 8px; font-size: 11px; } } }
</style>
